import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Globe, Pencil, BarChart3 } from 'lucide-react';
import { useMobile } from './hooks/useMobile';
import { useReducedMotion } from './hooks/useReducedMotion';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    number: '01',
    icon: Globe,
    iconColor: '#7C3AED',
    title: 'Builds Company Intelligence',
    description:
      'Paste your website URL and Zupa auto-extracts your brand identity, tone, audience, industry, and competitive landscape. No forms to fill, no questionnaires.',
  },
  {
    number: '02',
    icon: Pencil,
    iconColor: '#34D399',
    title: 'Defines Your Target Persona',
    description:
      'Create audience personas so every post speaks directly to the people you want to reach. Tailored messaging for each segment.',
  },
  {
    number: '03',
    icon: Globe,
    iconColor: '#60A5FA',
    title: 'Share Your Idea',
    description:
      'Drop in a rough thought — a sentence, a bullet point, a brain dump. Pick your platforms. Choose your tone. Hit generate.',
  },
  {
    number: '04',
    icon: BarChart3,
    iconColor: '#F59E0B',
    title: 'AI Applies Psychology',
    description:
      'Zupa layers 15+ behavioral psychology principles onto your content — scarcity, social proof, anchoring, loss aversion — each one selected for maximum impact.',
  },
  {
    number: '05',
    icon: Globe,
    iconColor: '#7C3AED',
    title: 'Platform Optimization',
    description:
      'Every post is reformatted for its target platform. What works on LinkedIn gets restructured for Instagram, X, and Facebook. No lazy cross-posting.',
  },
  {
    number: '06',
    icon: Pencil,
    iconColor: '#34D399',
    title: 'Publish Instantly',
    description:
      'Review your scored variations, pick your favorite, and publish directly to all connected platforms — or save to collections for later.',
  },
];

export function HowItWorksSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobile();
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Animate the connecting line
      if (lineRef.current && !isMobile) {
        gsap.from(lineRef.current, {
          scaleY: 0,
          transformOrigin: 'top center',
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
            end: 'bottom 60%',
            scrub: 1,
          },
        });
      }

      // Animate each step
      gsap.utils.toArray<HTMLElement>('.hiw-step').forEach((step, i) => {
        gsap.from(step, {
          opacity: 0,
          y: 40,
          duration: 0.7,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: step,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
          delay: i * 0.1,
        });
      });

      // Step icon scale-in
      gsap.utils.toArray<HTMLElement>('.hiw-icon').forEach((icon) => {
        gsap.from(icon, {
          scale: 0,
          duration: 0.5,
          ease: 'back.out(2)',
          scrollTrigger: {
            trigger: icon,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile, prefersReduced]);

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="relative overflow-hidden bg-landing-bg"
      style={{
        paddingTop: 'clamp(80px, 10vh, 160px)',
        paddingBottom: 'clamp(80px, 10vh, 160px)',
      }}
    >
      <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8">
        {/* Header */}
        <div className="mb-20 text-center">
          <p
            className="mb-4 text-zupa-purple-light"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 'clamp(0.9rem, 1vw, 1.1rem)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
            }}
          >
            The Process
          </p>
          <h2
            className="text-[#E8E6F0]"
            style={{
              fontFamily: "'Space Grotesk', system-ui, sans-serif",
              fontSize: 'clamp(2rem, 4vw + 0.5rem, 3.75rem)',
              fontWeight: 600,
              lineHeight: 1.1,
            }}
          >
            From Idea to Impact
          </h2>
          <p
            className="mt-4 text-[#C8C6D2]"
            style={{
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
              lineHeight: 1.65,
            }}
          >
            Six steps that transform raw thoughts into psychology-powered content.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line */}
          {!isMobile && (
            <div
              ref={lineRef}
              className="absolute left-[39px] top-0 bottom-0 w-px"
              style={{
                background: 'linear-gradient(to bottom, #7C3AED, #34D399, #F59E0B)',
                opacity: 0.3,
              }}
            />
          )}

          <div className="space-y-20 lg:space-y-28">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="hiw-step relative">
                  {/* Watermark number */}
                  <div
                    className="absolute -top-8 right-0 pointer-events-none select-none"
                    style={{
                      fontFamily: "'Space Grotesk', system-ui, sans-serif",
                      fontSize: 'clamp(5rem, 12vw, 10rem)',
                      fontWeight: 700,
                      color: 'rgba(232, 230, 240, 0.03)',
                      lineHeight: 1,
                    }}
                  >
                    {step.number}
                  </div>

                  <div className="relative flex gap-8 items-start">
                    {/* Icon */}
                    <div
                      className={cn(
                        'hiw-icon flex-shrink-0 flex items-center justify-center',
                        isMobile ? 'h-12 w-12' : 'h-[78px] w-[78px]'
                      )}
                      style={{
                        background: `${step.iconColor}12`,
                        border: `1px solid ${step.iconColor}25`,
                      }}
                    >
                      <Icon
                        size={isMobile ? 22 : 28}
                        style={{ color: step.iconColor }}
                      />
                    </div>

                    {/* Text */}
                    <div className="flex-1 pt-1">
                      <div
                        className="mb-1 text-[#C8C6D2]/50"
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          letterSpacing: '0.15em',
                        }}
                      >
                        STEP {step.number}
                      </div>
                      <h3
                        className="mb-3 text-[#E8E6F0]"
                        style={{
                          fontFamily: "'Space Grotesk', system-ui, sans-serif",
                          fontSize: 'clamp(1.5rem, 2.5vw + 0.5rem, 2rem)',
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {step.title}
                      </h3>
                      <p
                        className="max-w-[50ch] text-[#C8C6D2]"
                        style={{
                          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                          fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
                          lineHeight: 1.65,
                        }}
                      >
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
